<script setup>
import ControlBar from './ControlBar.vue'
import ModuleBar from './ModuleBar.vue'
import DraughtControl from './controls/draughtControl/DraughtControl.vue'
import EditionMode from './controls/EditionMode.vue'
import GetFeatureInfo from './controls/getFeatureInfo/GetFeatureInfo.vue'
import AisInfoBoard from './controls/AisInfoBoard.vue'
import ETALayer from './modules/ais/ETALayer.js'
import AttachedFiles from './common/AttachedFiles.vue'
import UlhyssesLogTracking from './modules/ulhysses/UlhyssesLogTracking.vue'
import { authorizedControl, authorizedModule } from '@/plugins/utils/Authorization.js'
import {
  CANCEL_ETA_CONTROL_EVENT,
  AIS_MESSAGE_RECEIVED_EVENT_TYPE,
  UPDATE_ETA_EVENT,
  CANCEL_EDITION_MODE_EVENT,
  SAVE_EDITION_MODE_EVENT,
  REMOVE_ETA_EVENT,
  SEND_MESSAGE_EVENT_TYPE,
  DRAW_ETA_EVENT,
  ACTIVE_SHIPPLACEMENT_ATTACHED_FILES_EVENT,
  INACTIVE_SHIPPLACEMENT_ATTACHED_FILES_EVENT
} from '@/plugins/utils/events.js'
import GeomodGraticule from '@/plugins/layers/GeomodGraticule'
import { httpRequest } from '@/plugins/utils/httpRequest.js'
import { usePortAllData } from '@/stores/portAllData'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n';
import { Point } from 'ol/geom'
import { transform } from 'ol/proj'
import { ref, computed, onMounted, onBeforeUnmount, inject, watch } from 'vue'
import { fromLonLat } from 'ol/proj'

const { t } = useI18n();
const map = ref(null)
const getFeatureInfo = ref(null)
const isMounted = ref(false)
const $map = inject('$map')
const $config = inject('$config')
const store = usePortAllData()
const { getAuthConfig, getVisibleLayers, getEditionMode, getAisData, showDraughtControl, getGraticuleIsActive, getIsMobile, getModuleProcessing } = storeToRefs(store)
const { setAisData, setEditionMode, setReplayAisData } = store
const modules = $config.modules
const controls = $config.controls
const dtsAllowed = ref(false)

const etaLayer = ref(null)

const infoBoardText = ref(null)

const graticuleLayer = ref(null)

const showDraught = ref(true)

const showSaveEdition = ref(true)

const shipDetailAttach = ref(null)
const showAttachedFiles = ref(false)

onMounted(async () => {
  const allLayers = $config.layers
  const profilLayers = getAuthConfig.value.authorizedLayers
  const profilRestraintArea = getAuthConfig.value.authorizedArea
  
  const allowedLayers = []
  for (const layer of allLayers) {
    if (profilLayers.includes(layer.privilege) || !layer.privilege) allowedLayers.push(layer)
  }
  
  $map.setMapLayers(allowedLayers)
  $map.setTarget(map.value)

  if (profilRestraintArea) $map.setRestraintArea(profilRestraintArea)

  // set visible layers from store
  const mapLayers = $map.getInitialLayerGroup().getLayers().getArray()
  for (const layer of mapLayers) {
    if (checkLayer(layer)) layer.setVisible(true)
    else layer.setVisible(false)
  }

  $map.setMapViewOptions($config.viewOptions);
  dtsAllowed.value = authorizedControl('draughtTideSlider', getAuthConfig.value.authorizedControls)
  $map.initControls($config.controls)

  document.addEventListener(AIS_MESSAGE_RECEIVED_EVENT_TYPE, aisMessaging)

  graticuleLayer.value = new GeomodGraticule();
  graticuleLayer.value.activateGraticule(getGraticuleIsActive.value ? $map : null);

  etaLayer.value = new ETALayer($map)
  $map.addLayer(etaLayer.value)

  document.addEventListener(SAVE_EDITION_MODE_EVENT, finishDrawETA)
  document.addEventListener(CANCEL_EDITION_MODE_EVENT, clearDrawETA)
  document.addEventListener(REMOVE_ETA_EVENT, removeETA)
  document.addEventListener(UPDATE_ETA_EVENT, updateETA)
  document.addEventListener(DRAW_ETA_EVENT, drawETA)

  document.addEventListener(ACTIVE_SHIPPLACEMENT_ATTACHED_FILES_EVENT, activeShipPlacementAttachedFiles)
  document.addEventListener(INACTIVE_SHIPPLACEMENT_ATTACHED_FILES_EVENT, inactiveShipPlacementAttachedFiles)

  if (eta.value && Object.keys(eta.value).length && Array.isArray(eta.value.way)) {
    etaLayer.value.addRoute(eta.value)
  }

  if (repeater.value && !infoBoardText.value) {
    infoBoardText.value = `${repeater.value.name} : ${t('wait-for-data')}`
  }

  isMounted.value = true

  if (window.location.hash.startsWith('#imo')) {
    const imo = window.location.hash.replace('#imo=', '');
    httpRequest(`/ais/ship/${imo}/position`, { method: 'GET' }, true, true).then((res) => {
      if (res?.coord) {
        const coordsT = transform(res.coord, 'EPSG:4326', 'EPSG:3857')
        const geom = new Point(coordsT)
        $map.getView().fit(geom, { size: $map.getSize(), duration: 500, maxZoom: 16 })
      }
    }).catch(e => {
      console.log('Bad MMSI')
    })
  }
})

const drawETA = (msg) => {
  if (eta.value) {
    etaLayer.value.clear(eta.value.id)
  }
  const detail = msg.detail
  if (detail) {
    showSaveEdition.value = false
    if (getIsMobile.value) document.dispatchEvent(new CustomEvent('closeFI'))
    setEditionMode(true)
    if (msg.from) detail.from = msg.from
    etaLayer.value.draw(detail)
  }
}

const finishDrawETA = () => {
  const ship = etaLayer.value.finish()
  if (ship) {
    if (ship.from === 'replay') setReplayAisData('eta', ship)
    else {
      setAisData('eta', ship)
      // subscribe
      subscribeShipData(ship.id, true, 'cogsog')
    }
  }
  showSaveEdition.value = true
  setEditionMode(false)
}

const removeETA = (e) => {
  if (e.detail?.id) {
    if (e.detail.from === 'replay') setReplayAisData('eta', null)
    else {
      setAisData('eta', null)
      subscribeShipData(e.detail.id, false, 'cogsog')
    }
    etaLayer.value.clear(e.detail.id)
  }
  showSaveEdition.value = true
}

const clearDrawETA = () => {
  etaLayer.value.clear()
  showSaveEdition.value = true
  setEditionMode(false)
  setAisData('eta', null)
  const event = new CustomEvent(CANCEL_ETA_CONTROL_EVENT);
  document.dispatchEvent(event);
}

const updateETA = ({ detail }) => {
  etaLayer.value.updateShip(detail)
  detail.way[0] = detail.coord
  setAisData('eta', detail)
}

const subscribeShipData = (shipId, isSubscription, property) => {
  const message = { target: 'ais', property }
  if (isSubscription) message.subscribe = shipId
  else message.unsubscribe = shipId
  const event = new CustomEvent(SEND_MESSAGE_EVENT_TYPE, { detail: message })
  document.dispatchEvent(event)
}

const checkLayer = (layer) => {
  return getVisibleLayers.value.includes(layer.getName()) || getVisibleLayers.value.includes(layer.get('merge'))
}

onBeforeUnmount(() => {
  document.removeEventListener(AIS_MESSAGE_RECEIVED_EVENT_TYPE, aisMessaging)
  document.removeEventListener(SAVE_EDITION_MODE_EVENT, finishDrawETA)
  document.removeEventListener(CANCEL_EDITION_MODE_EVENT, clearDrawETA)
  document.removeEventListener(DRAW_ETA_EVENT, drawETA)
  document.removeEventListener(REMOVE_ETA_EVENT, removeETA)
  document.removeEventListener(UPDATE_ETA_EVENT, updateETA)
  document.removeEventListener(ACTIVE_SHIPPLACEMENT_ATTACHED_FILES_EVENT, activeShipPlacementAttachedFiles)
  document.removeEventListener(INACTIVE_SHIPPLACEMENT_ATTACHED_FILES_EVENT, inactiveShipPlacementAttachedFiles)
})

const repeater = computed(() => {
  return getAisData.value.repeater || null
})

const eta = computed(() => {
  return getAisData.value.eta || null
})

const aisMessaging = (e) => {
  const { detail } = e
  const autoCenter = getAisData.value.center
  if (autoCenter && detail[autoCenter.id]) {
    const centerShip = detail[autoCenter.id]
    $map.getView().animateWithDelta({ center: fromLonLat(centerShip.coord), duration: 1000 })
  }
  if (repeater.value && detail[repeater.value.id]) {
    const repeaterShip = detail[repeater.value.id]
    if (repeaterShip) {
      infoBoardText.value = `${repeaterShip.name} ${repeaterShip.draught ? `(${repeaterShip.draught} m)` : ''}, ${repeaterShip.sog} kn, ${repeaterShip.cog}°`
    } else infoBoardText.value = null
  }

  if (eta.value && detail[eta.value.id]) {
    const etaInfo = detail[eta.value.id]
    const newCoords = fromLonLat(etaInfo.coord)
    const newEtaInfo = {
      ...eta.value,
      coord: newCoords
    }
    const event = new CustomEvent(UPDATE_ETA_EVENT, { detail: newEtaInfo })
    document.dispatchEvent(event)
  }
}

const activeShipPlacementAttachedFiles = (e) => {
  if (e.detail?.attachedFiles?.length) {
    shipDetailAttach.value = e.detail
    showAttachedFiles.value = true
  } else {
    shipDetailAttach.value = null
    showAttachedFiles.value = false
  }
}

const inactiveShipPlacementAttachedFiles = () => {
  showAttachedFiles.value = false
  shipDetailAttach.value = null
}

watch(getGraticuleIsActive, (nv) => {
  graticuleLayer.value.activateGraticule(nv ? $map : null);
})

const showUlhLogs = computed(() => {
  const isAuthorized = authorizedModule('ulhysses', getAuthConfig.value.authorizedModules)
  const codeUlh = modules?.ulhysses?.code ? modules.ulhysses.code : 115
  return isAuthorized && getModuleProcessing.value[codeUlh]
})
</script>

<template>
  <div ref="map" class="mapContainer">
    <control-bar v-if="isMounted" />
    <module-bar v-if="isMounted" v-model:showDraught="showDraught" />
    <draught-control v-if="dtsAllowed && showDraughtControl && showDraught" :draught-tide-options="controls.draughtTideSlider" />
    <edition-mode v-if="getEditionMode" :show-save="showSaveEdition" />
    <get-feature-info v-if="controls.getFeatureInfo.enabled" ref="getFeatureInfo" />
    <ais-info-board v-if="repeater && infoBoardText" :ais-text="infoBoardText" />
    <attached-files v-if="showAttachedFiles" :ship-detail-attach="shipDetailAttach" />
    <ulhysses-log-tracking v-if="showUlhLogs" />
  </div>
  <div id="background-images-container" class="bg-images-container" />
</template>

<i18n src="@/locales/modulesControls.json"></i18n>